import React, { useEffect, useRef, useState } from 'react'
import { Box, ButtonBase, Grid, useTheme } from '@mui/material'
import { PlatformEvent } from 'lib/util'
import { formatFilesize } from 'lib/util/attachment'
import { useStyles } from 'platform/react/hook'
import AttachmentListTemplateButton from 'ui/Element/Attachment/List/Template/Button'
import OverflowTooltip from 'ui/Element/Text/OverflowTooltip'
import Icon from 'ui/Element/Icon'

const styles = theme => ({
  root: {
    height: '100%',
    flexWrap: 'nowrap',
    flexDirection: 'column',
  },
  toolbar: {
    flexBasis: 'auto',
    color: theme.palette.black.main,
    backgroundColor: theme.palette.white.main,
    borderRadius: '0.5rem',
    padding: '0.5rem',
    alignItems: 'center',
    marginBottom: '1rem',
  },
  button: {
    minWidth: '2rem',
    width: '2rem!important',
    height: '2rem!important',
    backgroundColor: theme.palette.background.content,
    borderRadius: '0.25rem',
    '&:disabled': {
      backgroundColor: theme.palette.white.main,
      '& > *': {
        backgroundColor: theme.palette.border.main,
      },
    },
    '& > *': {
      margin: 0,
    },
    padding: 0,
    minHeight: 0,
  },
  title: {
    display: 'flex',
    flexDirection: 'row',
    gap: '0.5rem',
    justifyContent: 'space-around',
    alignItems: 'center',
  },
  column: {
    [theme.breakpoints.down('sm')]: {
      height: 'auto',
    },
    height: '100%',
    display: 'flex',
    gap: '0.5rem',
  },
})

/**
 * Helper function to create an icon
 * @param {Object} icon       the icon
 * @param {string|null} color the color for the icon
 * @returns {Element}
 * @private
 */
const _createIcon = ({ name, color }) => {
  const theme = useTheme()
  return (
    <Icon
      name={name}
      size={'1.5rem'}
      color={color || theme.palette.black.main}
    />
  )
}

/**
 * Toolbar HOC
 *
 * Wraps `Component` with a toolbar if `showToolbar` is truthy. If not, simply returns
 * `Component` without doing anything. The toolbar will display a download button by default
 * if the attachment is a document and not a mere url.
 *
 * Will optionally display a feedback button if `showFeedback` is truthy. Requires
 * `onFeedbackOpen` event handler to be present in `events`.
 *
 * @param {React.ReactNode} Component the attachment component to render
 * @returns {function({showToolbar: *, events: *, [p: string]: *}): React.JSX.Element}
 */
const Toolbar = Component => ({ showToolbar, events, ...props }) => {
  const theme = useTheme()
  const titleRef = useRef()

  const [titleWidth, setTitleWidth] = useState(null)

  const { attachment, showFeedback, showFavorite } = props
  const {
    onFeedbackOpen = null,
    onFavoriteOpen = null,
    onFavoriteClick = null,
    ...restEvents
  } = events || {}

  const classes = useStyles(styles)()

  const currentAttachment = attachment?.value?.name
    ? attachment
    : { value: { name: '' } }
  const { value: item } = currentAttachment

  const handleFeedbackOpen = (e) => {
    onFeedbackOpen?.(new PlatformEvent(e, { payload: item?.documentationId || item?.key }))
  }

  const favoriteId = item?.documentationId || item?.key
  const isFavorite = onFavoriteOpen?.(new PlatformEvent('open', { value: favoriteId }))
  const favoriteEvent = e => new PlatformEvent(e, { payload: favoriteId, options: { delete: !!isFavorite, type: 'documentation' } })

  // Dynamically calculating the width based on the parents height
  const resizeHandler = () => { setTitleWidth(titleRef?.current?.clientWidth) }

  // Recalculating height if we resize the window
  useEffect(() => {
    window.addEventListener('resize', resizeHandler)
    return () => { window.removeEventListener('resize', resizeHandler) }
  }, [])

  useEffect(() => { titleRef?.current && setTitleWidth(titleRef?.current.clientWidth) }, [titleRef])

  const feedbackIcon = _createIcon({ name: props.feedbackIcon || 'message-text' })
  const favoriteIcon = _createIcon({ name: props.favoriteIcon || 'favorite-filled', color: 'signal.main' })
  const noFavoriteIcon = _createIcon({ name: props.noFavoriteIcon || 'favorite' })

  return !showToolbar
    ? <Component
      events={events}
      {...props}
    />
    : (
      <Grid
        item
        xs={12}
        container
        className={classes.root}
      >
        <Grid
          item
          container
          xs={12}
          className={classes.toolbar}
        >
          <Grid
            item
            sm={2}
            xs={12}
            className={classes.column}
            sx={{ justifyContent: 'flex-start' }}
          >
            {/* Optional left column options */}
          </Grid>
          <Grid
            item
            sm={8}
            xs={12}
            ref={titleRef}
            className={classes.column}
            sx={{ justifyContent: 'space-around' }}
          >
            <Box
              className={classes.title}
              sx={{ ...titleWidth && { maxWidth: titleWidth } }}
            >
              <OverflowTooltip
                variant={'14/bold'}
                sx={{ color: 'black.main' }}
              >
                {attachment?.value?.name || ''}
              </OverflowTooltip>
              {attachment.value?.size && (
                <OverflowTooltip
                  variant={'14/medium'}
                  sx={{ color: theme.palette.grey['400'] }}
                >
                  {formatFilesize(attachment.value.size)}
                </OverflowTooltip>
              )}
            </Box>
          </Grid>
          <Grid
            item
            sm={2}
            xs={12}
            className={classes.column}
            sx={{ justifyContent: 'flex-end' }}
          >
            {!item.url && (
              <AttachmentListTemplateButton
                value={null}
                group={props.group}
                attachment={attachment}
                icon={props.downloadIcon || 'download'}
                className={classes.button}
              />
            )}
            {showFeedback && (
              <ButtonBase
                onClick={handleFeedbackOpen}
                className={classes.button}
              >
                {feedbackIcon}
              </ButtonBase>
            )}
            {showFavorite && (
              <ButtonBase
                onClick={e => onFavoriteClick?.(favoriteEvent(e))}
                className={classes.button}
              >
                {isFavorite ? favoriteIcon : noFavoriteIcon}
              </ButtonBase>
            )}
          </Grid>
        </Grid>
        <Component
          {...props}
          events={restEvents}
        />
      </Grid>
    )
}

export default Component => Toolbar(Component)
